import React, { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { BreadcrumbNavigation } from '../components/BreadcrumbNavigation';
import { SeoHead } from '../components/SeoHead';

/**
 * ProgrammaticPage
 * Dynamic SEO landing pages for categories, states, and qualifications.
 */
export function ProgrammaticPage({ jobPosts, darkMode }) {
  const location = useLocation();
  const path = location.pathname.toLowerCase().replace('/', '');

  // Define configuration mapping for routes
  const config = useMemo(() => ({
    'ssc-jobs': { title: 'SSC Jobs 2026', desc: 'Latest Staff Selection Commission (SSC) Jobs, CGL, CHSL, MTS.', keyword: 'SSC' },
    'railway-jobs': { title: 'Railway Jobs 2026', desc: 'Latest RRB NTPC, Group D, ALP Railway Jobs.', keyword: 'Railway' },
    'bank-jobs': { title: 'Bank Jobs 2026', desc: 'Latest IBPS, SBI, RBI Bank Jobs and PO/Clerk vacancies.', keyword: 'Bank' },
    'upsc-jobs': { title: 'UPSC Jobs 2026', desc: 'Latest UPSC Civil Services, NDA, CDS Jobs.', keyword: 'UPSC' },
    'teaching-jobs': { title: 'Teaching Jobs 2026', desc: 'Latest TET, PGT, TGT, PRT Teaching Jobs.', keyword: 'Teach' },
    'delhi-government-jobs': { title: 'Delhi Government Jobs', desc: 'Latest DSSSB and Delhi State Govt Jobs.', keyword: 'Delhi' },
    'up-government-jobs': { title: 'UP Government Jobs', desc: 'Latest UPPSC, UPSSSC, UP Police Jobs.', keyword: 'UP ' }, // Space to avoid matching UPSC
    'bihar-government-jobs': { title: 'Bihar Government Jobs', desc: 'Latest BPSC, Bihar Police, BSEB Jobs.', keyword: 'Bihar' },
    'jobs-after-10th': { title: 'Government Jobs After 10th Pass', desc: 'Latest Govt Jobs for 10th Pass (High School).', keyword: '10th' },
    'jobs-after-12th': { title: 'Government Jobs After 12th Pass', desc: 'Latest Govt Jobs for 12th Pass (Intermediate).', keyword: '12th' },
    'jobs-after-graduation': { title: 'Government Jobs After Graduation', desc: 'Latest Govt Jobs for Graduates (BA, BSc, BCom, BTech).', keyword: 'Graduat' },
  }), []);

  const currentConfig = config[path] || { title: 'Latest Government Jobs', desc: 'Find the latest Sarkari Naukri.', keyword: '' };

  const filteredJobs = useMemo(() => {
    if (!currentConfig.keyword) return jobPosts.slice(0, 50);
    
    const kw = currentConfig.keyword.toLowerCase();
    return jobPosts.filter(job => {
      const textToSearch = `${job.title} ${job.department} ${job.shortDescription} ${job.eligibilityDetails?.join(' ')}`.toLowerCase();
      return textToSearch.includes(kw);
    }).slice(0, 50);
  }, [jobPosts, currentConfig.keyword]);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <SeoHead 
        title={`${currentConfig.title} | Sarkari Result`} 
        description={currentConfig.desc}
        canonical={`https://www.sarkariresult.cloud/${path}`}
      />
      <div className="max-w-4xl mx-auto px-3 py-4 sm:p-6">
        <BreadcrumbNavigation paths={[
          { label: 'Home', link: '/' },
          { label: currentConfig.title, link: `/${path}` }
        ]} darkMode={darkMode} />
        
        <div className={`p-6 sm:p-8 rounded-lg shadow-md mb-8 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h1 className={`text-2xl sm:text-3xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {currentConfig.title}
          </h1>
          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
            {currentConfig.desc} Browse the list of the most recently updated and active recruitment notifications below.
          </p>

          <div className="space-y-4">
            {filteredJobs.length > 0 ? filteredJobs.map(job => {
              const slug = job.seoSlug || String(job.title || job.department || "details").toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
              return (
                <div key={job.id} className={`p-4 rounded-lg border ${darkMode ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-200 hover:bg-gray-50'} transition-colors`}>
                  <Link to={`/job/${job.id}/${slug}`} className={`text-lg font-semibold hover:underline line-clamp-2 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                    {job.tileDisplayTitle || job.title}
                  </Link>
                  <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{job.department}</p>
                </div>
              );
            }) : (
              <p className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No active jobs found for this category currently.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
